import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { AndroidDevice, Doc, IosDevice, ModelClass, References } from '/imports/types';
import { userDisplayName } from '/imports/api/users';
import { validateExists, validateString } from '/imports/validators';
import { DeclareMethod } from '/imports/methods';
import { pluck } from '/imports/utils';
import { Orgs } from '/imports/api/orgs';
import { ErrorCode } from '/imports/errors';
import { strings } from '/imports/strings';

export interface Account extends Doc {
  _id: string;
  nid: number;
  active?: boolean;
  lastActiveState?: boolean;
  deleted?: boolean;
  orgId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  roles?: string[];
  androidDevices?: AndroidDevice[];
  iosDevices?: IosDevice[];
  _user?: { _id: string, username?: string, firstName?: string, lastName?: string };
  _org?: { _id: string, name?: string };
}

type Model = Account;
type Refs = References<Model>;

export const Accounts = new Mongo.Collection<Account>('accounts');

Meteor.isServer && Meteor.startup(async () => {
  await Accounts.createIndexAsync({ orgId: 1, userId: 1 }, { unique: true, sparse: true });
});

export const Roles = {
  admin: 'admin',
  dispatcher: 'dispatcher',
};

export const validateAccount = async (account: Partial<Account>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, account, 'orgId', { required: true });
  account.orgId && await validateExists(errors, 'orgId', Orgs, account.orgId);
  validateString(errors, account, 'userId', { required: true });
  account.userId && await validateExists(errors, 'userId', Meteor.users, account.userId);
  validateString(errors, account, 'firstName', { required: true });
  validateString(errors, account, 'lastName', { required: true });
  validateString(errors, account, 'email');
  validateString(errors, account, 'phone');
  return errors;
};

const fields = [
  'nid', 'active', 'deleted', 'orgId', 'userId', 'firstName', 'lastName', 'email', 'phone', 'roles',
];

export const cleanAccount = (account: Partial<Account>, customFields?: string[]) => {
  const _fields = customFields || fields;
  return pluck(account, _fields);
};

type CreateAccountReturn = { accountId: string, email: string, createdUser?: boolean | { username?: string }, newRoles: string[] };

export const listAccountsForOrg = DeclareMethod<string | void, Account[]>('listAccountsForOrg');
export const getAccount = DeclareMethod<string, Account>('getAccount');
export const createAccount = DeclareMethod<Partial<Account>, CreateAccountReturn>('createAccount');
export const updateAccount = DeclareMethod<Partial<Account>, string>('updateAccount');
export const updateAccountProfile = DeclareMethod<Partial<Account>>('updateAccountProfile');
export const deleteAccount = DeclareMethod<string>('deleteAccount');
export const getAccountUser = DeclareMethod<string, Meteor.User>('getAccountUser');
export const accountUsername = DeclareMethod<string, string>('accountUsername');
interface UpdateAccountCredentials { accountId: string, credentials: { username?: string, password?: string } }
export const updateAccountCredentials = DeclareMethod<UpdateAccountCredentials>('updateAccountCredentials');
export const getAccountForUser = DeclareMethod<string, Account>('getAccountForUser');

export const canAdminAccounts = async (user?: Meteor.User | null, orgId?: string) => {
  if (!user) return false;
  if (user?.isSuperUser) return true;
  orgId = orgId || user.orgId;
  if (!orgId) return false;
  const account = await Accounts.findOneAsync({ userId: user._id, orgId });
  if (account?.roles?.includes(Roles.admin)) return true;
  return false;
};

export const canListAccounts = async (user?: Meteor.User | null, orgId?: string) => {
  return await canAdminAccounts(user, orgId);
};

export const accountsSearchQuery = (search?: string) => {
  search = search?.trim();
  if (!search?.length) return {};
  return {
    $or: [
      { _id: search },
      { firstName: new RegExp(search, 'gi') },
      { lastName: new RegExp(search, 'gi') },
      { email: new RegExp(search, 'gi') },
      { phone: new RegExp(search, 'gi') },
      { phone: new RegExp(search, 'gi') },
    ]
  };
};

export const accountDisplayName = <T extends Pick<Account, '_id' | 'firstName' | 'lastName' | 'userId' | '_user'>>(account?: T) => {
  if (!account) return '';
  if (account.firstName && account.lastName) return `${account.firstName} ${account.lastName}`;
  const user = account._user;
  return user && userDisplayName(user) || account._id;
};

export const accountForOrg = async (user?: Meteor.User | null, orgId?: string) => {
  if (user && orgId) return await Accounts.findOneAsync({ orgId, userId: user._id }) || undefined;
};

export const adminGuard = async (orgId?: string, throws?: boolean) => {
  const user = await Meteor.userAsync();
  if (!user || !orgId) throw new Meteor.Error(ErrorCode.forbidden);
  if (user?.isSuperUser) return true;
  if (user.orgId !== orgId) {
    if (throws) throw new Meteor.Error(ErrorCode.forbidden);
    return false;
  }
  const account = await Accounts.findOneAsync({ userId: user._id, orgId });
  if (account?.roles?.includes(Roles.admin)) return account;
  if (throws) throw new Meteor.Error(ErrorCode.forbidden);
  return false;
};

// Allows: super user, admin role, or dispatcher role. For dispatch operations
// (loads, drivers, sms messages).
export const dispatcherGuard = async () => {
  const user = await Meteor.userAsync();
  if (!user) throw new Meteor.Error(ErrorCode.unauthorized);
  if (user.isSuperUser) return user;
  const account = user.orgId ? await Accounts.findOneAsync({ userId: user._id, orgId: user.orgId }) : undefined;
  if (account?.roles?.includes(Roles.admin) || account?.roles?.includes(Roles.dispatcher)) return user;
  throw new Meteor.Error(ErrorCode.forbidden);
};

// Allows: super user or admin role only. For management operations
// (carriers, vendors, users, accounts) that dispatchers should NOT perform.
export const adminOrSuperGuard = async () => {
  const user = await Meteor.userAsync();
  if (!user) throw new Meteor.Error(ErrorCode.unauthorized);
  if (user.isSuperUser) return user;
  const account = user.orgId ? await Accounts.findOneAsync({ userId: user._id, orgId: user.orgId }) : undefined;
  if (account?.roles?.includes(Roles.admin)) return user;
  throw new Meteor.Error(ErrorCode.forbidden);
};

export const currentAccount = async (user?: Meteor.User | null) => {
  user = user || await Meteor.userAsync();
  const userId = user?._id;
  const orgId = user?.orgId;
  if (!orgId || !userId) return;
  return await Accounts.findOneAsync({ orgId, userId });
};

export const modelClass: ModelClass<Model, Refs> = {
  collection: Accounts,
  editableFields: fields,
  listMethod: () => [] as any,
  getMethod: () => null as any,
  createMethod: () => '' as any,
  updateMethod: updateAccount,
  deleteMethod: deleteAccount,
  pathPrefix: '/users',
  asString: (document) => accountDisplayName(document),
  modelName: strings.Account(),
  pluralName: strings.Accounts(),
  searchQuery: accountsSearchQuery,
  validate: validateAccount,
};